import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
  Stack,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useWarbandStore } from '../../stores/warbandStore';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { Weapon, Armour, SpecialTag } from '../../types';
import { ConfirmActionDialog } from '../shared/ConfirmActionDialog';
import { SelectSpecialTagsModal } from './SelectSpecialTagsModal';

type ViewMode = 'list' | 'form';
type TabIndex = 0 | 1;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseNum(value: string): number | null {
  if (value.trim() === '') return null;
  const n = Number(value);
  return isNaN(n) ? null : n;
}

// ---------------------------------------------------------------------------
// Weapon form
// ---------------------------------------------------------------------------

interface WeaponForm {
  name: string;
  damageModifier: string;
  maxRange: string;
  encumbering: boolean;
  slotsConsumed: string;
  selectedTags: SpecialTag[];
}

const emptyWeaponForm = (): WeaponForm => ({
  name: '',
  damageModifier: '0',
  maxRange: '',
  encumbering: true,
  slotsConsumed: '1',
  selectedTags: [],
});

function weaponToForm(w: Weapon): WeaponForm {
  return {
    name: w.name,
    damageModifier: w.damageModifier !== null ? String(w.damageModifier) : '0',
    maxRange: w.maxRange !== null ? String(w.maxRange) : '',
    encumbering: w.encumbering === 1,
    slotsConsumed: w.slotsConsumed !== null ? String(w.slotsConsumed) : '1',
    selectedTags: w.specialTags,
  };
}

// ---------------------------------------------------------------------------
// Armour form
// ---------------------------------------------------------------------------

interface ArmourForm {
  name: string;
  armourModifier: string;
  encumbering: boolean;
  selectedTags: SpecialTag[];
}

const emptyArmourForm = (): ArmourForm => ({
  name: '',
  armourModifier: '0',
  encumbering: true,
  selectedTags: [],
});

function armourToForm(a: Armour): ArmourForm {
  return {
    name: a.name,
    armourModifier: a.armourModifier !== null ? String(a.armourModifier) : '0',
    encumbering: a.encumbering === 1,
    selectedTags: a.specialTags,
  };
}

// ---------------------------------------------------------------------------
// Weapons tab
// ---------------------------------------------------------------------------

function WeaponsTab() {
  const { customWeapons, addCustomWeapon, updateCustomWeapon, deleteCustomWeapon } =
    useReferenceDataStore();

  const [view, setView] = useState<ViewMode>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<WeaponForm>(emptyWeaponForm);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [tagModalOpen, setTagModalOpen] = useState(false);

  const setField = <K extends keyof WeaponForm>(k: K, v: WeaponForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleNew = () => {
    setEditingId(null);
    setForm(emptyWeaponForm());
    setView('form');
  };

  const handleEdit = (w: Weapon) => {
    setEditingId(w.id);
    setForm(weaponToForm(w));
    setView('form');
  };

  const handleSave = () => {
    const data: Omit<Weapon, 'id' | 'isCustom'> = {
      name: form.name.trim(),
      damageModifier: parseNum(form.damageModifier) ?? 0,
      maxRange: parseNum(form.maxRange),
      encumbering: form.encumbering ? 1 : null,
      slotsConsumed: parseNum(form.slotsConsumed) ?? 1,
      notes: null,
      description: null,
      specialTags: form.selectedTags,
    };

    if (editingId) {
      updateCustomWeapon(editingId, data);
    } else {
      addCustomWeapon(data);
    }
    setView('list');
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteCustomWeapon(deleteTarget.id);
    setDeleteTarget(null);
  };

  if (view === 'form') {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
          <IconButton size="small" onClick={() => setView('list')} aria-label="back">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">{editingId ? 'Edit Weapon' : 'New Weapon'}</Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              fullWidth
              required
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField
              label="Damage Modifier"
              type="number"
              value={form.damageModifier}
              onChange={(e) => setField('damageModifier', e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField
              label="Max Range"
              type="number"
              value={form.maxRange}
              onChange={(e) => setField('maxRange', e.target.value)}
              fullWidth
              size="small"
              placeholder="None"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField
              label="Slots Consumed"
              type="number"
              value={form.slotsConsumed}
              onChange={(e) => setField('slotsConsumed', e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.encumbering}
                  onChange={(e) => setField('encumbering', e.target.checked)}
                />
              }
              label="Encumbering"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setTagModalOpen(true)}>
                Add Special Tag
              </Button>
            </Box>
            {form.selectedTags.length > 0 && (
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                {form.selectedTags.map((tag) => (
                  <Chip
                    key={tag.id}
                    label={tag.id}
                    size="small"
                    onDelete={() => setField('selectedTags', form.selectedTags.filter((t) => t.id !== tag.id))}
                  />
                ))}
              </Stack>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={() => setView('list')}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name.trim()}>
            {editingId ? 'Update' : 'Create'}
          </Button>
        </Box>

        <SelectSpecialTagsModal
          open={tagModalOpen}
          excludeTagIds={form.selectedTags.map((t) => t.id)}
          onClose={() => setTagModalOpen(false)}
          onSelect={(tag) => {
            setField('selectedTags', [...form.selectedTags, tag]);
            setTagModalOpen(false);
          }}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button startIcon={<AddIcon />} onClick={handleNew} size="small">
          New Weapon
        </Button>
      </Box>
      {customWeapons.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No custom weapons yet.
        </Typography>
      ) : (
        <List dense disablePadding>
          {customWeapons.map((w) => (
            <ListItem
              key={w.id}
              divider
              secondaryAction={
                <Stack direction="row" gap={0.5}>
                  <IconButton size="small" onClick={() => handleEdit(w)} aria-label="edit">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeleteTarget({ id: w.id, name: w.name })}
                    aria-label="delete"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              }
            >
              <ListItemText
                primary={w.name}
                secondary={[
                  w.damageModifier !== null ? `DMG ${w.damageModifier > 0 ? '+' : ''}${w.damageModifier}` : null,
                  w.maxRange !== null ? `Range ${w.maxRange}"` : null,
                  w.specialTags.length > 0 ? w.specialTags.map((t) => t.id).join(', ') : null,
                ]
                  .filter(Boolean)
                  .join(' · ') || undefined}
              />
            </ListItem>
          ))}
        </List>
      )}

      <ConfirmActionDialog
        open={!!deleteTarget}
        title="Delete Weapon"
        message={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Armour tab
// ---------------------------------------------------------------------------

function ArmourTab() {
  const { customArmours, addCustomArmour, updateCustomArmour, deleteCustomArmour } =
    useReferenceDataStore();

  const [view, setView] = useState<ViewMode>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ArmourForm>(emptyArmourForm);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [tagModalOpen, setTagModalOpen] = useState(false);

  const setField = <K extends keyof ArmourForm>(k: K, v: ArmourForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleNew = () => {
    setEditingId(null);
    setForm(emptyArmourForm());
    setView('form');
  };

  const handleEdit = (a: Armour) => {
    setEditingId(a.id);
    setForm(armourToForm(a));
    setView('form');
  };

  const handleSave = () => {
    const data: Omit<Armour, 'id' | 'isCustom'> = {
      name: form.name.trim(),
      armourModifier: parseNum(form.armourModifier) ?? 0,
      encumbering: form.encumbering ? 1 : null,
      notes: null,
      description: null,
      specialTags: form.selectedTags,
    };

    if (editingId) {
      updateCustomArmour(editingId, data);
    } else {
      addCustomArmour(data);
    }
    setView('list');
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteCustomArmour(deleteTarget.id);
    setDeleteTarget(null);
  };

  if (view === 'form') {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
          <IconButton size="small" onClick={() => setView('list')} aria-label="back">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">{editingId ? 'Edit Armour' : 'New Armour'}</Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              fullWidth
              required
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              label="Armour Modifier"
              type="number"
              value={form.armourModifier}
              onChange={(e) => setField('armourModifier', e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.encumbering}
                  onChange={(e) => setField('encumbering', e.target.checked)}
                />
              }
              label="Encumbering"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setTagModalOpen(true)}>
                Add Special Tag
              </Button>
            </Box>
            {form.selectedTags.length > 0 && (
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                {form.selectedTags.map((tag) => (
                  <Chip
                    key={tag.id}
                    label={tag.id}
                    size="small"
                    onDelete={() => setField('selectedTags', form.selectedTags.filter((t) => t.id !== tag.id))}
                  />
                ))}
              </Stack>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={() => setView('list')}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name.trim()}>
            {editingId ? 'Update' : 'Create'}
          </Button>
        </Box>

        <SelectSpecialTagsModal
          open={tagModalOpen}
          excludeTagIds={form.selectedTags.map((t) => t.id)}
          onClose={() => setTagModalOpen(false)}
          onSelect={(tag) => {
            setField('selectedTags', [...form.selectedTags, tag]);
            setTagModalOpen(false);
          }}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button startIcon={<AddIcon />} onClick={handleNew} size="small">
          New Armour
        </Button>
      </Box>
      {customArmours.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No custom armour yet.
        </Typography>
      ) : (
        <List dense disablePadding>
          {customArmours.map((a) => (
            <ListItem
              key={a.id}
              divider
              secondaryAction={
                <Stack direction="row" gap={0.5}>
                  <IconButton size="small" onClick={() => handleEdit(a)} aria-label="edit">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeleteTarget({ id: a.id, name: a.name })}
                    aria-label="delete"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              }
            >
              <ListItemText
                primary={a.name}
                secondary={[
                  a.armourModifier !== null ? `Armour ${a.armourModifier > 0 ? '+' : ''}${a.armourModifier}` : null,
                  a.specialTags.length > 0 ? a.specialTags.map((t) => t.id).join(', ') : null,
                ]
                  .filter(Boolean)
                  .join(' · ') || undefined}
              />
            </ListItem>
          ))}
        </List>
      )}

      <ConfirmActionDialog
        open={!!deleteTarget}
        title="Delete Armour"
        message={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Modal wrapper
// ---------------------------------------------------------------------------

export function ManageCustomEquipmentModal() {
  const { ui, closeCustomEquipmentModal } = useWarbandStore();
  const [tabIndex, setTabIndex] = useState<TabIndex>(0);

  return (
    <Dialog
      open={ui.customEquipmentModalOpen}
      onClose={closeCustomEquipmentModal}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Custom Equipment</DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={(_e, v) => setTabIndex(v as TabIndex)}
          sx={{ mb: 2 }}
        >
          <Tab label="Weapons" />
          <Tab label="Armour" />
        </Tabs>
        {tabIndex === 0 && <WeaponsTab />}
        {tabIndex === 1 && <ArmourTab />}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeCustomEquipmentModal}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
